// 工序生产演示（/process-demo/:name）
// 工艺流程页 19 个工序的下级页面：左侧 16:9 演示视频 + 右侧工序要点说明
// 由 2223 移植：2221 舞台 7680×2160，横向/字号 ×2，纵向按 2160 预算收敛
<template>
  <div class="pd-page" :style="pageStyle">
    <div class="pd-viewport" ref="viewport">
      <div class="pd-scale-box" ref="box">
        <div class="pd-stage" ref="stage">
          <!-- 左上工序名 -->
          <div class="pd-title-row">
            <h1 class="pd-title">
              {{ step.name }}<i v-if="step.sub" class="pd-title-sub">{{ step.sub }}</i>
              <em class="pd-tag" :class="'pd-tag-' + tagKind">{{ tagText }}</em>
            </h1>
            <button class="pd-back" type="button" @click="goBack">
              <span class="pd-back-arrow">←</span> {{ $C('processDemo', 'back', '返回工艺流程') }}
            </button>
          </div>

          <!-- 主体：16:9 演示视频 + 右侧工序说明 -->
          <div class="pd-body">
            <div class="pd-screen">
              <video
                class="pd-video"
                :src="videoSrc"
                autoplay
                muted
                loop
                playsinline
                controls
              ></video>
              <span class="pd-badge"><i></i>{{ $C('processDemo', 'demoBadge', '生产演示视频') }} · {{ step.name }}</span>
            </div>

            <aside class="pd-panel">
              <div class="pd-panel-head">
                <span class="pd-panel-title">{{ $C('processDemo', 'panelTitle', '工序要点') }}</span>
                <span class="pd-panel-sub">STEP {{ stepNo }} / 19</span>
              </div>
              <p class="pd-desc">{{ step.desc }}</p>
              <ul class="pd-keys">
                <li v-for="(k, i) in stepKeys" :key="i">
                  <i class="pd-key-dot"></i>{{ k }}
                </li>
              </ul>
              <p class="pd-note">{{ $C('processDemo', 'note', '演示视频为框架期占位片源，接入实际工位摄像机位后按工序替换视频地址即可。') }}</p>
            </aside>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { PROCESS_STEPS, STEP_KEYS } from '@/data/processFlow';
import { mediaUrl } from '@/cms/config';

export default {
  name: 'processDemo',
  data() {
    return { scale: 1 };
  },
  computed: {
    step() {
      let name = this.$route.params.name || '';
      try { name = decodeURIComponent(name); } catch (e) { /* 保持原值 */ }
      return PROCESS_STEPS.find(s => s.name === name) || PROCESS_STEPS[0];
    },
    stepNo() {
      const mains = PROCESS_STEPS.filter(s => s.branch === '');
      const i = mains.findIndex(s => s.key === this.step.key);
      return i > -1 ? i + 1 : '—';
    },
    stepKeys() {
      const map = this.$C('processDemo', 'stepKeys', STEP_KEYS);
      return map[this.step.key] || [];
    },
    tagText() {
      if (this.step.branch === 'reject') return '不合格处理';
      if (this.step.reject) return '检验工序';
      if (this.step.key === 'store') return '合格放行';
      return '主线工序';
    },
    tagKind() {
      if (this.step.branch === 'reject') return 'reject';
      if (this.step.key === 'store') return 'ok';
      return 'main';
    },
    videoSrc() {
      // 后台「工序演示视频」分片可逐工序配置视频（stepVideos：按工序 key 填写视频地址，留空则用内置占位片源）
      const map = this.$C('processDemo', 'stepVideos', [])
      const row = (map || []).find(x => x && x.key === this.step.key)
      const v = (row && row.video) || process.env.BASE_URL + 'assets/ai/ai.webm'
      return /^(\/)?media\//.test(v) ? mediaUrl(v) : v
    },
    pageStyle() {
      // 可选页面背景图：后台配置后覆盖默认渐变；未配置时回退到 CSS 默认背景
      const bg = this.$C('processDemo', 'pageBg', '');
      return bg ? { backgroundImage: `url(${mediaUrl(bg)})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {};
    },
  },
  mounted() {
    this.$nextTick(() => this.calc());
    window.addEventListener('resize', this.calc);
  },
  activated() {
    this.$nextTick(() => this.calc());
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calc);
  },
  methods: {
    goBack() {
      if (window.history.length > 1) this.$router.back();
      else this.$router.replace('/process-flow');
    },
    calc() {
      const vp = this.$refs.viewport;
      const stage = this.$refs.stage;
      const box = this.$refs.box;
      if (!vp || !stage || !box) return;
      const s = Math.min(vp.clientWidth / 7680, vp.clientHeight / 2160);
      this.scale = s;
      stage.style.transform = `scale(${s})`;
      box.style.width = 7680 * s + 'px';
      box.style.height = 2160 * s + 'px';
    },
  },
};
</script>

<style lang="less" scoped>
.pd-page {
  position: fixed;
  inset: 0;
  z-index: 1010; /* 覆盖 process-flow 全屏层 */
  background:
    radial-gradient(1100px 560px at 35% 100%, rgba(16, 60, 130, 0.4), rgba(0, 0, 0, 0) 70%),
    linear-gradient(165deg, #02091a 0%, #051228 55%, #02091a 100%);
  overflow: hidden;
  font-family: 'Microsoft YaHei', 'PingFang SC', sans-serif;
}
.pd-viewport {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.pd-scale-box { position: relative; }
.pd-stage {
  width: 7680px;
  height: 2160px;
  transform-origin: top left;
  padding: 130px 180px 110px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 70px;
}

.pd-title-row {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.pd-title {
  display: flex;
  align-items: baseline;
  font-size: 112px;
  font-weight: 700;
  letter-spacing: 12px;
  color: #fff;
  text-shadow: 0 0 36px rgba(80, 200, 255, 0.7);
  .pd-title-sub {
    font-style: normal;
    font-size: 64px;
    font-weight: 400;
    letter-spacing: 4px;
    color: rgba(150, 205, 255, 0.85);
    margin-left: 48px;
  }
}
.pd-tag {
  font-style: normal;
  font-size: 56px;
  font-weight: 400;
  letter-spacing: 6px;
  margin-left: 68px;
  padding: 12px 52px;
  border-radius: 999px;
  vertical-align: middle;
}
.pd-tag-main { color: #7de9ff; border: 2px solid rgba(90, 210, 255, 0.7); background: rgba(20, 80, 150, 0.45); }
.pd-tag-reject { color: #ffc46b; border: 2px solid rgba(255, 190, 100, 0.7); background: rgba(110, 70, 20, 0.5); }
.pd-tag-ok { color: #4dffa0; border: 2px solid rgba(80, 255, 160, 0.7); background: rgba(20, 90, 60, 0.5); }
.pd-back {
  background: rgba(0, 229, 255, 0.12);
  border: 2px solid rgba(0, 229, 255, 0.55);
  color: #00E5FF;
  font-size: 60px;
  letter-spacing: 6px;
  padding: 28px 72px;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover { background: rgba(0, 229, 255, 0.25); box-shadow: 0 0 36px rgba(0, 229, 255, 0.5); }
}
.pd-back-arrow { margin-right: 12px; }

.pd-body {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  gap: 100px;
}

/* ---------- 16:9 演示视频 ---------- */
.pd-screen {
  flex: none;
  width: 2900px;
  height: 1631px; /* 2900 / 16 * 9 = 1631 */
  position: relative;
  border: 5px solid rgba(90, 170, 255, 0.85);
  border-radius: 52px;
  overflow: hidden;
  background: #000;
  box-shadow: 0 0 80px rgba(40, 130, 255, 0.35), inset 0 0 60px rgba(0, 20, 60, 0.6);
}
.pd-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.pd-badge {
  position: absolute;
  left: 80px;
  top: 68px;
  display: flex;
  align-items: center;
  gap: 28px;
  font-size: 60px;
  letter-spacing: 6px;
  color: #fff;
  text-shadow: 0 4px 16px rgba(0, 0, 0, 0.9);
  i {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #ff5348;
    box-shadow: 0 0 24px rgba(255, 90, 70, 0.95);
    animation: pd-blink 1.4s ease-in-out infinite;
  }
}
@keyframes pd-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.25; } }

/* ---------- 右侧工序说明 ---------- */
.pd-panel {
  flex: 1;
  min-width: 0;
  height: 1631px;
  border: 6px solid rgba(70, 150, 255, 0.9);
  border-radius: 88px;
  background: rgba(6, 20, 46, 0.72);
  box-shadow: 0 0 68px rgba(50, 130, 255, 0.4), inset 0 0 80px rgba(20, 70, 160, 0.25);
  padding: 140px 100px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 60px;
}
.pd-panel-head {
  flex: none;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  border-bottom: 2px solid rgba(90, 160, 255, 0.35);
  padding-bottom: 50px;
}
.pd-panel-title {
  font-size: 88px;
  font-weight: 700;
  letter-spacing: 12px;
  color: #fff;
  text-shadow: 0 0 28px rgba(80, 200, 255, 0.7);
}
.pd-panel-sub {
  font-size: 56px;
  letter-spacing: 6px;
  color: rgba(150, 205, 255, 0.85);
  font-family: 'DIN Alternate', 'Arial', sans-serif;
}
.pd-desc {
  flex: none;
  font-size: 60px;
  line-height: 100px;
  letter-spacing: 4px;
  color: #eaf3ff;
}
.pd-keys {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 56px;
  li {
    display: flex;
    align-items: center;
    gap: 44px;
    font-size: 56px;
    line-height: 84px;
    letter-spacing: 4px;
    color: rgba(200, 228, 255, 0.95);
  }
}
.pd-key-dot {
  flex: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #31FFFF;
  box-shadow: 0 0 24px rgba(49, 224, 255, 0.9);
}
.pd-note {
  flex: none;
  font-size: 48px;
  line-height: 80px;
  letter-spacing: 2px;
  color: rgba(150, 190, 240, 0.8);
  border-top: 2px solid rgba(90, 160, 255, 0.3);
  padding-top: 56px;
}
</style>
